// KontaktSuche.tsx — „Für welchen Kontakt?" in den Vorfrage-Dialogen des
// „+“-Menüs. Reine Suche (keine Neuanlage — dafür gibt es den Eintrag
// „Kontakt" im selben Menü), als Combobox bedienbar: tippen, ↑/↓, Enter.
//
// Die Suche selbst kommt als Funktion herein (`suchen`), damit die Tests ohne
// Netz laufen; im Header ist es `GET /contact?search=` wie im
// RelationContactPicker.

import * as React from "react";
import { People20Regular, Search20Regular } from "@fluentui/react-icons";

export type KontaktTreffer = { id: number; name: string; typ?: string | null };

export type KontaktSucheFn = (text: string, signal: AbortSignal) => Promise<KontaktTreffer[]>;

const MIN_ZEICHEN = 2;
const VERZOEGERUNG_MS = 250;

export function KontaktSuche({
  gewaehlt,
  onWaehlen,
  suchen,
  autoFocus,
}: {
  gewaehlt: KontaktTreffer | null;
  onWaehlen: (k: KontaktTreffer | null) => void;
  suchen: KontaktSucheFn;
  autoFocus?: boolean;
}) {
  const [text, setText] = React.useState("");
  const [treffer, setTreffer] = React.useState<KontaktTreffer[]>([]);
  const [laedt, setLaedt] = React.useState(false);
  const [fehler, setFehler] = React.useState(false);
  const [aktiv, setAktiv] = React.useState(0);
  const listId = React.useId();

  React.useEffect(() => {
    const t = text.trim();
    if (t.length < MIN_ZEICHEN) {
      setTreffer([]);
      setLaedt(false);
      setFehler(false);
      return;
    }
    const ctrl = new AbortController();
    setLaedt(true);
    const timer = window.setTimeout(() => {
      suchen(t, ctrl.signal)
        .then((liste) => {
          if (ctrl.signal.aborted) return;
          setTreffer(liste);
          setAktiv(0);
          setFehler(false);
        })
        .catch(() => {
          if (!ctrl.signal.aborted) {
            setTreffer([]);
            setFehler(true);
          }
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setLaedt(false);
        });
    }, VERZOEGERUNG_MS);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [text, suchen]);

  if (gewaehlt) {
    return (
      <div className="na-gewaehlt">
        <span className="na-gewaehlt-ico" aria-hidden>
          <People20Regular />
        </span>
        <div className="na-gewaehlt-text">
          <div className="na-gewaehlt-label">Kontakt</div>
          <div className="na-gewaehlt-name">{gewaehlt.name}</div>
        </div>
        <button type="button" className="na-link" onClick={() => onWaehlen(null)}>
          Ändern
        </button>
      </div>
    );
  }

  const beiTaste = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!treffer.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAktiv((a) => Math.min(treffer.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAktiv((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const k = treffer[aktiv];
      if (k) onWaehlen(k);
    }
  };

  const t = text.trim();
  return (
    <div>
      <div className="na-suche">
        <Search20Regular aria-hidden />
        <input
          type="text"
          role="combobox"
          aria-label="Kontakt suchen"
          aria-expanded={treffer.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={treffer.length ? `${listId}-${aktiv}` : undefined}
          placeholder="Name, Firma oder Kundennummer …"
          autoFocus={autoFocus}
          autoComplete="off"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={beiTaste}
        />
      </div>
      {treffer.length > 0 ? (
        <ul className="na-treffer" role="listbox" id={listId} aria-label="Gefundene Kontakte">
          {treffer.map((k, i) => (
            <li
              key={k.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === aktiv}
              className="na-treffer-zeile"
              onMouseEnter={() => setAktiv(i)}
              // mousedown statt click: das Eingabefeld behält den Fokus nicht
              // länger als nötig, und die Wahl kommt vor einem Blur an.
              onMouseDown={(e) => {
                e.preventDefault();
                onWaehlen(k);
              }}
            >
              <span className="na-treffer-name">{k.name}</span>
              {k.typ ? <span className="na-treffer-typ">{k.typ}</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="na-leise" aria-live="polite">
          {t.length < MIN_ZEICHEN
            ? "Mindestens zwei Buchstaben eingeben."
            : laedt
              ? "Suche …"
              : fehler
                ? "Die Suche ist gerade nicht erreichbar."
                : "Kein Kontakt gefunden."}
        </div>
      )}
    </div>
  );
}

export default KontaktSuche;
